import * as THREE from 'three';
import type { ExperienceCue, Speaker, ThoughtIdentityConfig } from '../content/types';

export class ThoughtField {
  readonly group = new THREE.Group();
  private readonly provider = this.createProviderField();
  private readonly patient = this.createPatientField();
  private active: Speaker | null = null;
  private reducedMotion = false;
  private config?: ThoughtIdentityConfig;
  private baseScale = 0.72;
  private activeCue: string | null = null;
  private energy = 0;
  private previousElapsed = 0;
  private readonly wave: THREE.Line;

  constructor() {
    this.group.name = 'thought-identity-field';
    this.group.add(this.provider, this.patient);
    this.group.visible = false;
    this.provider.add(this.createLabel('BETH · THINKING'));
    this.patient.add(this.createLabel('MARIA · THINKING'));
    this.wave = this.patient.getObjectByName('voice-wave') as THREE.Line;
  }

  configure(config: ThoughtIdentityConfig): void { this.config = config; }

  setCue(cue: ExperienceCue | null): void {
    if (this.activeCue === (cue?.id ?? null)) return;
    this.activeCue = cue?.id ?? null;
    this.active = cue?.kind === 'thought' ? (cue.speaker ?? null) : null;
    this.group.visible = this.active !== null;
    this.provider.visible = this.active === 'provider';
    this.patient.visible = this.active === 'patient';
    const anchor = cue ? this.config?.anchors[cue.id] : undefined;
    if (anchor && this.active) {
      const activeGroup = this.active === 'provider' ? this.provider : this.patient;
      const x = (anchor.u - 0.5) * 9.2;
      activeGroup.position.set(x, 0.08 + (0.5 - anchor.v) * 5.175, -5.62 + 0.42 * (x / 4.6) ** 2);
      activeGroup.rotation.set(0, 0, 0);
      this.baseScale = anchor.width * 9.2 / 3.05;
      activeGroup.scale.setScalar(this.baseScale);
    } else if (cue?.anchor) {
      const activeGroup = this.active === 'provider' ? this.provider : this.patient;
      this.placeAtAnchor(activeGroup, cue.anchor.yawDeg, cue.anchor.pitchDeg);
    }
  }

  setReducedMotion(reduced: boolean): void {
    this.reducedMotion = reduced;
    if (reduced) {
      this.provider.rotation.z = 0;
      this.patient.rotation.z = 0;
    }
  }

  update(elapsed: number, audioEnergy: number, playing = true): void {
    if (!this.active) return;
    const dt = Math.max(0, Math.min(0.1, elapsed - this.previousElapsed));
    this.previousElapsed = elapsed;
    this.energy = playing ? this.energy + (audioEnergy - this.energy) * (1 - Math.exp(-dt * 12)) : 0;
    audioEnergy = this.reducedMotion ? 0 : this.energy;
    const activeGroup = this.active === 'provider' ? this.provider : this.patient;
    const breath = this.reducedMotion ? 1 : 1 + Math.sin(elapsed * 1.18) * 0.008 + audioEnergy * 0.045;
    activeGroup.scale.setScalar(this.baseScale * breath);
    activeGroup.rotation.z = this.reducedMotion
      ? 0
      : Math.sin(elapsed * (this.active === 'provider' ? 0.34 : 0.28)) * 0.012;
    activeGroup.traverse((object) => {
      if (!(object instanceof THREE.Line || object instanceof THREE.Mesh)) return;
      const material = object.material as THREE.Material & { opacity?: number };
      if (!('opacity' in material)) return;
      if (material.userData.identityLabel) return;
      const baseOpacity = Number(material.userData.baseOpacity ?? 0.38);
      material.opacity = Math.min(0.82, baseOpacity + audioEnergy * 0.2);
    });
    this.wave.scale.y = this.reducedMotion ? 1 : 0.6 + audioEnergy * 2.5;
  }

  dispose(): void {
    this.group.traverse((object) => {
      if (!(object instanceof THREE.Mesh || object instanceof THREE.Line)) return;
      object.geometry.dispose();
      const materials = Array.isArray(object.material) ? object.material : [object.material];
      materials.forEach((material) => { if ('map' in material) (material.map as THREE.Texture | null)?.dispose(); material.dispose(); });
    });
  }

  private createProviderField(): THREE.Group {
    const group = new THREE.Group();
    group.name = 'provider-open-arc-field';
    const arcs = [
      { y: -0.7, lift: 0.78, opacity: 0.46 },
      { y: -0.26, lift: 0.98, opacity: 0.4 },
      { y: 0.2, lift: 0.88, opacity: 0.34 },
      { y: 0.64, lift: 0.64, opacity: 0.28 },
    ];

    for (const [index, arc] of arcs.entries()) {
      const curve = new THREE.CatmullRomCurve3([
        new THREE.Vector3(-1.45, arc.y - 0.12, 0),
        new THREE.Vector3(-0.86, arc.y + arc.lift * 0.56, 0.015),
        new THREE.Vector3(0.15, arc.y + arc.lift, 0.025),
        new THREE.Vector3(0.94, arc.y + arc.lift * 0.64, 0.015),
        new THREE.Vector3(1.45, arc.y + 0.08, 0),
      ], false, 'centripetal');
      const material = new THREE.MeshBasicMaterial({
        color: index < 2 ? '#eaf5fb' : '#59b7df',
        transparent: true,
        opacity: arc.opacity,
        depthTest: false,
        toneMapped: false,
      });
      material.userData.baseOpacity = arc.opacity;
      const ribbon = new THREE.Mesh(new THREE.TubeGeometry(curve, 52, 0.014, 6, false), material);
      ribbon.position.z = -index * 0.018;
      group.add(ribbon);
    }

    const centerMaterial = new THREE.MeshBasicMaterial({ color: '#eaf5fb', transparent: true, opacity: 0.5, depthTest: false, toneMapped: false });
    centerMaterial.userData.baseOpacity = 0.5;
    for (const [x, y] of [[-0.72, 0.06], [0.05, 0.42], [0.76, 0.05]] as Array<[number, number]>) {
      const point = new THREE.Mesh(new THREE.CircleGeometry(0.035, 18), centerMaterial.clone());
      (point.material as THREE.Material).userData.baseOpacity = 0.5;
      point.position.set(x, y, 0.05);
      group.add(point);
    }
    return group;
  }

  private createPatientField(): THREE.Group {
    const group = new THREE.Group();
    group.name = 'patient-soft-ripple-field';
    for (let index = 0; index < 5; index += 1) {
      const opacity = 0.46 - index * 0.055;
      const material = new THREE.MeshBasicMaterial({
        color: index < 2 ? '#eaf5fb' : '#59b7df',
        transparent: true,
        opacity,
        side: THREE.DoubleSide,
        depthTest: false,
        toneMapped: false,
      });
      material.userData.baseOpacity = opacity;
      const ring = new THREE.Mesh(
        new THREE.RingGeometry(0.52 + index * 0.25, 0.535 + index * 0.25, 96, 1, 0.42, Math.PI * 1.58),
        material,
      );
      ring.rotation.z = -0.38 + index * 0.21;
      ring.position.z = -index * 0.018;
      group.add(ring);
    }

    const breathPoints = Array.from({ length: 54 }, (_, index) => {
      const x = -1.28 + (index / 53) * 2.56;
      const envelope = 1 - Math.min(1, Math.abs(x) / 1.38);
      return new THREE.Vector3(x, Math.sin(index * 0.48) * 0.055 * envelope, 0.04);
    });
    const breathMaterial = new THREE.LineBasicMaterial({ color: '#eaf5fb', transparent: true, opacity: 0.46, depthTest: false, toneMapped: false });
    breathMaterial.userData.baseOpacity = 0.46;
    const wave = new THREE.Line(new THREE.BufferGeometry().setFromPoints(breathPoints), breathMaterial);
    wave.name = 'voice-wave';
    group.add(wave);
    return group;
  }

  private createLabel(text: string): THREE.Mesh {
    const canvas = document.createElement('canvas');
    canvas.width = 600; canvas.height = 96;
    const ctx = canvas.getContext('2d')!;
    ctx.fillStyle = 'rgba(21,36,86,.88)';
    ctx.beginPath(); ctx.roundRect(2,2,596,92,20); ctx.fill();
    ctx.strokeStyle = '#59b7df'; ctx.lineWidth = 3; ctx.stroke();
    ctx.fillStyle = '#ffffff'; ctx.font = '600 35px Arial'; ctx.textAlign = 'center';
    ctx.fillText(text,300,61);
    const map = new THREE.CanvasTexture(canvas); map.colorSpace = THREE.SRGBColorSpace;
    const material = new THREE.MeshBasicMaterial({map,transparent:true,depthTest:false,toneMapped:false});
    material.userData.identityLabel = true;
    const label = new THREE.Mesh(new THREE.PlaneGeometry(2.4,.384),material);
    label.position.set(0,1.94,.08); label.renderOrder = 30;
    return label;
  }

  private placeAtAnchor(group: THREE.Group, yawDeg: number, pitchDeg: number): void {
    const radius = 4.4;
    const yaw = THREE.MathUtils.degToRad(yawDeg);
    const pitch = THREE.MathUtils.degToRad(pitchDeg);
    group.position.set(Math.sin(yaw) * radius, Math.sin(pitch) * radius, -Math.cos(yaw) * radius);
    group.lookAt(0, 0, 0);
    group.rotateY(Math.PI);
  }
}
